import { useRef, useState, useEffect } from "react";
import OpenAI from "openai";
import { useApiKey } from "../ApiKeyContext";
import GameRulesCard from "./GameRulesCard";
import "./GameRulesCard.css";
import "./SoloDetective.css";
import detectiveLogo from "../assets/logo-detective.png";

type SoloDetectiveProps = {
  onQuit: () => void;
};

type Objet = {
  nom: string;
  description: string;
  débloqué_si: string[];
  débloque?: string[];
};
type Zone = {
  nom: string;
  description: string;
  objets: Objet[];
};
type DialogueEtape = {
  étape: number;
  questions: {
    texte: string;
    réponse: string;
    débloque?: string[];
  }[];
  débloqué_si?: string[];
};
type Suspect = {
  nom: string;
  rôle: string;
  personnalité: string;
  comportement_observable: string;
  dialogue: DialogueEtape[];
};

type EnqueteData = {
  lieu: string;
  zones: Zone[];
  suspects: Suspect[];
  états_initiaux: string[];
  coupable: string;
  mobile: string;
  explication_finale: string;
};

export default function SoloDetective({ onQuit }: SoloDetectiveProps) {
  const apiKey = useApiKey();
  const openai = new OpenAI({ apiKey, dangerouslyAllowBrowser: true });
  const assistantId = "asst_kMx5Ut2ZHasr1ePx6W9MPWJz";
  const threadId = useRef<string | null>(null);

  const [showRules, setShowRules] = useState(true);
  const [loading, setLoading] = useState(false);
  const [jsonResult, setJsonResult] = useState(""); // Pour le debug
  const [enquete, setEnquete] = useState<EnqueteData | null>(null);
  const [showDebug, setShowDebug] = useState(false);

  // Etats du jeu
  const [etats, setEtats] = useState<string[]>([]);
  const [currentLieu, setCurrentLieu] = useState<string | null>(null);
  const [objetsVus, setObjetsVus] = useState<string[]>([]);
  const [objetActif, setObjetActif] = useState<Objet | null>(null);

  // Dialogue interactif
  const [suspectActif, setSuspectActif] = useState<Suspect | null>(null);
  const [questionsPosees, setQuestionsPosees] = useState<Record<string, boolean>>({});
  const [reponsesAffichees, setReponsesAffichees] = useState<Record<string, string>>({});

  // ----- APPEL GPT & GENERATION -----
  const ask = async (prompt: string): Promise<string> => {
    if (!threadId.current) {
      const t = await openai.beta.threads.create();
      threadId.current = t.id;
    }
    await openai.beta.threads.messages.create(threadId.current, {
      role: "user",
      content: prompt,
    });
    const run = await openai.beta.threads.runs.createAndPoll(threadId.current, {
      assistant_id: assistantId,
    });
    const messages = await openai.beta.threads.messages.list(threadId.current);
    const message = messages.data.find((m) => m.run_id === run.id && m.role === "assistant");
    const textBlock = message?.content.find((c) => c.type === "text");
    return textBlock && "text" in textBlock ? textBlock.text.value : "";
  };

  const handleGenerate = async () => {
    setLoading(true);
    setEnquete(null);
    setJsonResult("");
    setCurrentLieu(null);
    setEtats([]);
    setObjetsVus([]);
    setQuestionsPosees({});
    setReponsesAffichees({});
    setObjetActif(null);
    setSuspectActif(null);

    let finalText = "";
    let prompt = "tu dois generer une enquete";
    let continueLoop = true;
    while (continueLoop) {
      const res = await ask(prompt);
      const trimmed = res.trim();
      finalText += trimmed.replace(/\[\.\.\.\]$/, "").trim() + "\n";
      continueLoop = trimmed.endsWith("[...]");
      prompt = "vas y continue";
    }
    setJsonResult(finalText.trim());
    try {
      const obj = JSON.parse(finalText.trim());
      setEnquete(obj);
      setCurrentLieu(obj.zones?.[0]?.nom || null);
      setEtats([...(obj.états_initiaux || []), `entrée_dans_${obj.zones?.[0]?.nom}`]);
      setObjetsVus([]);
      setQuestionsPosees({});
      setReponsesAffichees({});
      setObjetActif(null);
      setSuspectActif(null);
    } catch (e) {
      alert("Erreur parsing enquête !");
    }
    setLoading(false);
  };

  // Ajoute systématiquement l'état "entrée_dans_[lieu]" dès qu'on change de salle
  useEffect(() => {
    if (currentLieu) {
      setEtats(etats =>
        etats.includes(`entrée_dans_${currentLieu}`) ? etats : [...etats, `entrée_dans_${currentLieu}`]
      );
    }
    // eslint-disable-next-line
  }, [currentLieu]);

  // Changement de lieu : ne change que la salle (l'état sera ajouté automatiquement par useEffect)
  function goToLieu(lieu: string) {
    setCurrentLieu(lieu);
    setSuspectActif(null);
    setObjetActif(null);
  }

  // Objets accessibles selon états
  function objetsDisponibles(zone: Zone) {
    if (!zone) return [];
    return zone.objets.filter(obj =>
      !obj.débloqué_si?.length || obj.débloqué_si.every(cond => etats.includes(cond))
    );
  }

  // Quand on clique sur un objet
  function trouverObjet(obj: Objet) {
    if (obj.débloque) {
      setEtats(etats => [
        ...etats,
        ...obj.débloque!.filter(e => !!e && !etats.includes(e))
      ]);
    }
    setObjetsVus(arr => arr.includes(obj.nom) ? arr : [...arr, obj.nom]);
    setObjetActif(obj); // Affiche la popup persistante
  }

  // Suspects accessibles dans ce lieu (étape 1 accessible et "entrée_dans_lieu" dans débloqué_si)
  function suspectsAccessibles() {
    if (!enquete || !currentLieu) return [];
    return enquete.suspects.filter(susp =>
      susp.dialogue.some(
        etape =>
          (!etape.débloqué_si || etape.débloqué_si.every(cond => etats.includes(cond)))
          && (etape.étape === 1)
          && (
            !etape.débloqué_si ||
            etape.débloqué_si.includes(`entrée_dans_${currentLieu}`)
          )
      )
    );
  }

  // Toutes les questions d’un suspect, étapes débloquées, triées non posées en haut puis posées en bas
  function allQuestionsForSuspect(suspect: Suspect) {
    // Toutes les étapes débloquées (par ordre d'étape)
    const etapesDebloquees = suspect.dialogue
      .filter(d => !d.débloqué_si || d.débloqué_si.every(cond => etats.includes(cond)))
      .sort((a, b) => a.étape - b.étape);

    // Rassemble toutes les questions avec leur étape et leur index
    let questions: {
      étape: number;
      index: number;
      texte: string;
      réponse: string;
      débloque?: string[];
    }[] = [];
    etapesDebloquees.forEach(etape =>
      etape.questions.forEach((q, idx) =>
        questions.push({ étape: etape.étape, index: idx, ...q })
      )
    );

    // Sépare non posées / posées, tout en gardant l'ordre des étapes
    const newQuestions = questions.filter(q =>
      !questionsPosees[suspect.nom + "-" + q.étape + "-" + q.index]
    );
    const askedQuestions = questions.filter(q =>
      questionsPosees[suspect.nom + "-" + q.étape + "-" + q.index]
    );

    // Retourne : d'abord les nouvelles, puis les posées (grisé)
    return [...newQuestions, ...askedQuestions];
  }

  // Gestion question posée
  function handlePoseQuestion(suspectNom: string, idx: number, q: { texte: string; réponse: string; débloque?: string[]; }, etape: number) {
    const key = suspectNom + "-" + etape + "-" + idx;
    setQuestionsPosees(qp => ({ ...qp, [key]: true }));
    setReponsesAffichees(ra => ({ ...ra, [key]: q.réponse }));
    if (q.débloque) {
      setEtats(etats => [
        ...etats,
        ...q.débloque!.filter(e => !!e && !etats.includes(e))
      ]);
    }
  }

  // ----- UI -----
  return (
    <div className="container" style={{ minHeight: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button className="quit-button" onClick={onQuit} disabled={loading} title="Quitter">
          ❌
        </button>
        <button className="debug-button" onClick={() => setShowDebug(x => !x)} title="Voir JSON">
          🐞
        </button>
      </div>
      {showDebug && (
        <pre
          className="debug-json"
        >
          {jsonResult}
        </pre>
      )}
      {showRules ? (
        <GameRulesCard
          logo={detectiveLogo}
          gameName="Enquête Solo"
          rules={[
            "▸GPT va générer une enquête solo avec lieux, objets et suspects.",
            "▸Les objets, dialogues et suspects se débloquent selon tes trouvailles.",
            "▸À toi de résoudre l'enquête en interrogeant tout le monde !",
          ]}
          onNext={() => setShowRules(false)}
          loading={loading}
        />
      ) : loading ? (
        <p className="loading">GPT génère l'enquête…</p>
      ) : !enquete ? (
        <button className="fullWidth" onClick={handleGenerate} disabled={loading}>
          Générer une enquête
        </button>
      ) : (
        <>
          <h2 className="titre-jeu">
            <span role="img" aria-label="detective">🕵️</span> Enquête Solo
          </h2>
          {/* Lieux */}
          <div className="lieux-row">
            {enquete.zones.map(zone => (
              <button
                key={zone.nom}
                className={currentLieu === zone.nom ? "selected" : ""}
                onClick={() => goToLieu(zone.nom)}
              >
                {zone.nom}
              </button>
            ))}
          </div>

          {/* Description du lieu */}
          <div style={{ marginBottom: 12 }}>
            <b style={{ fontSize: 18 }}>{currentLieu}</b>
            <br />
            {enquete.zones.find(z => z.nom === currentLieu)?.description}
          </div>

          {/* Objets disponibles */}
          <div style={{ marginBottom: 12 }}>
            <b>Objets à examiner :</b>
            <div className="objets-row">
              {objetsDisponibles(enquete.zones.find(z => z.nom === currentLieu)!).map(obj => (
                <button
                  key={obj.nom}
                  onClick={() => trouverObjet(obj)}
                  style={{
                    opacity: objetsVus.includes(obj.nom) ? 0.54 : 1,
                    filter: objetsVus.includes(obj.nom) ? "grayscale(50%)" : "none"
                  }}
                  title={obj.description}
                  tabIndex={0}
                >
                  {obj.nom}
                </button>
              ))}
            </div>
          </div>

          {/* Popup objet persistante */}
          {objetActif && (
            <div className="popup-objet">
              <div className="popup-titre">{objetActif.nom}</div>
              <div>{objetActif.description}</div>
              <button
                className="popup-fermer"
                onClick={() => setObjetActif(null)}
                title="Fermer"
              >✖</button>
            </div>
          )}

          {/* Suspects */}
          <div style={{ marginBottom: 12 }}>
            <b>Suspects :</b>
            <div className="suspects-row">
              {suspectsAccessibles().map(s => (
                <button
                  key={s.nom}
                  onClick={() => {
                    setSuspectActif(s);
                  }}
                >
                  {s.nom}
                </button>
              ))}
            </div>
          </div>

          {/* Dialogue suspect interactif */}
          {suspectActif && (
            <div className="dialogue-suspect">
              <h4>{suspectActif.nom} <span className="suspect-role">({suspectActif.rôle})</span></h4>
              <p className="suspect-desc">{suspectActif.personnalité}</p>
              <p className="suspect-comp"><b>Comportement :</b> {suspectActif.comportement_observable}</p>
              <div>
                <b>Questions :</b>
                <div className="questions-col">
                  {allQuestionsForSuspect(suspectActif).map((q, idx) => {
                    const key = suspectActif.nom + "-" + q.étape + "-" + q.index;
                    const posed = !!questionsPosees[key];
                    return (
                      <div key={key} className={posed ? "question-posee" : ""}>
                        <button
                          className="btn-question"
                          style={{
                            filter: posed ? "grayscale(60%)" : "none",
                            fontStyle: posed ? "italic" : "normal",
                            fontWeight: posed ? 400 : 600
                          }}
                          disabled={false}
                          onClick={() => {
                            handlePoseQuestion(suspectActif.nom, q.index, q, q.étape);
                          }}
                        >
                          {q.texte}
                          <span className="question-badge">
                            {posed ? "Déjà posée" : `Étape ${q.étape}`}
                          </span>
                        </button>
                        {reponsesAffichees[key] && (
                          <div className="reponse-dialogue">
                            <b>Réponse : </b>{reponsesAffichees[key]}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <button className="fermer-dialogue-btn" onClick={() => setSuspectActif(null)} style={{ marginTop: 14 }}>
                Fermer
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
